/**
 * NewsView - News articles display with TTS
 * Shows aggregated news from RSS feeds
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Newspaper, Volume2, Loader, ChevronRight, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import useTTS from '../../hooks/useTTS';

const API = process.env.REACT_APP_BACKEND_URL;

export default function NewsView({ onBack, senior }) {
  const [globalArticles, setGlobalArticles] = useState([]);
  const [regionalArticles, setRegionalArticles] = useState([]);
  const [regionalInfo, setRegionalInfo] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Use centralized TTS hook
  const { speak, stop, isSpeaking, isLoading: isTTSLoading } = useTTS({
    language: senior?.language || 'de',
    deviceId: senior?.device_id || 'default'
  });

  useEffect(() => {
    fetchNews();
  }, [senior]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch global news
      const globalResponse = await axios.get(`${API}/api/news/?limit=20`);
      setGlobalArticles(globalResponse.data.articles || []);
      console.log('[News] Loaded', globalResponse.data.articles.length, 'global articles');
      
      // Fetch regional news if senior ID is available
      if (senior?.id) {
        try {
          const regionalResponse = await axios.get(`${API}/api/news/regional/${senior.id}?limit=10`);
          
          if (regionalResponse.data.articles && regionalResponse.data.articles.length > 0) {
            setRegionalArticles(regionalResponse.data.articles);
            setRegionalInfo({
              region: regionalResponse.data.region,
              postalCode: regionalResponse.data.postal_code,
              message: regionalResponse.data.message
            });
            console.log('[News] Loaded', regionalResponse.data.articles.length, 'regional articles for', regionalResponse.data.region);
          } else {
            // No regional articles, but might have a message
            setRegionalInfo({
              region: regionalResponse.data.region,
              postalCode: regionalResponse.data.postal_code,
              message: regionalResponse.data.message
            });
            console.log('[News] No regional articles:', regionalResponse.data.message);
          }
        } catch (regionalErr) {
          console.error('[News] Regional error:', regionalErr);
          // Don't set main error, just log it
        }
      }
      
    } catch (err) {
      console.error('[News] Error:', err);
      setError('Nachrichten konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const readAloud = (text) => {
    if (!text) {
      toast.error('Kein Text zum Vorlesen');
      return;
    }
    speak(text);
  };

  const stopReading = () => {
    stop();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Loader className="w-16 h-16 mx-auto mb-4 animate-spin text-blue-600" />
          <p className="text-2xl text-gray-600">Nachrichten werden geladen...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <Newspaper className="w-24 h-24 mx-auto mb-4 text-gray-400" />
          <p className="text-2xl text-gray-600 mb-6">{error}</p>
          <button
            onClick={fetchNews}
            className="px-8 py-4 bg-blue-500 hover:bg-blue-600 text-white rounded-xl text-xl font-bold"
          >
            Erneut versuchen
          </button>
        </div>
      </div>
    );
  }

  // Detail View
  if (selectedArticle) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="bg-white rounded-3xl shadow-2xl p-8">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <button
              onClick={() => {
                stopReading();
                setSelectedArticle(null);
              }}
              className="px-6 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl text-xl font-bold"
            >
              ← Zurück
            </button>
            
            <button
              onClick={() => {
                if (isSpeaking) {
                  stopReading();
                } else {
                  const fullText = `${selectedArticle.title}. ${selectedArticle.description || ''}`;
                  readAloud(fullText);
                }
              }}
              className={`px-6 py-3 rounded-xl text-xl font-bold flex items-center gap-3 ${
                isSpeaking 
                  ? 'bg-red-500 hover:bg-red-600 text-white' 
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              <Volume2 className="w-6 h-6" />
              {isSpeaking ? 'Stop' : 'Vorlesen'}
            </button>
          </div>

          {/* Image */}
          {selectedArticle.image_url && (
            <img
              src={selectedArticle.image_url}
              alt={selectedArticle.title}
              className="w-full h-64 object-cover rounded-2xl mb-6"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          )}

          {/* Content */}
          <div className="space-y-4">
            <div className="text-sm text-blue-600 font-semibold">
              {selectedArticle.source} • {new Date(selectedArticle.published_at).toLocaleDateString('de-DE')}
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 leading-tight">
              {selectedArticle.title}
            </h1>
            
            <p className="text-2xl text-gray-700 leading-relaxed">
              {selectedArticle.description}
            </p>
            
            {selectedArticle.content && (
              <div className="text-xl text-gray-600 leading-relaxed mt-6">
                {selectedArticle.content}
              </div>
            )}

            <a
              href={selectedArticle.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block mt-6 px-6 py-3 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded-xl text-lg font-semibold"
            >
              Vollständigen Artikel lesen →
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Helper to render article list
  const renderArticleList = (articles) => (
    <div className="space-y-4">
      {articles.map((article) => (
        <button
          key={article.id}
          onClick={() => setSelectedArticle(article)}
          className="w-full bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all p-6 text-left group"
        >
          <div className="flex gap-6">
            {/* Image */}
            {article.image_url && (
              <img
                src={article.image_url}
                alt={article.title}
                className="w-32 h-32 object-cover rounded-xl flex-shrink-0"
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            )}
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="text-sm text-blue-600 font-semibold mb-2">
                {article.source}
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors">
                {article.title}
              </h3>
              <p className="text-lg text-gray-600 line-clamp-2">
                {article.description}
              </p>
              <div className="mt-3 text-sm text-gray-500">
                {new Date(article.published_at).toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                })}
              </div>
            </div>
            
            <ChevronRight className="w-8 h-8 text-gray-400 group-hover:text-blue-600 transition-colors flex-shrink-0" />
          </div>
        </button>
      ))}
    </div>
  );

  // List View
  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Nachrichten</h1>
        <p className="text-xl text-gray-600">
          {regionalArticles.length > 0 && regionalInfo?.region && (
            <span className="text-green-600 font-semibold">{regionalInfo.region} • </span>
          )}
          {globalArticles.length + regionalArticles.length} aktuelle Artikel
        </p>
      </div>

      {/* Regional News Section */}
      {regionalArticles.length > 0 && (
        <div className="mb-12">
          <div className="mb-4 flex items-center gap-3">
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-gray-900">
                📍 Regionale Nachrichten
              </h2>
              {regionalInfo && (
                <p className="text-lg text-gray-600 mt-1">
                  {regionalInfo.region}
                  {regionalInfo.postalCode && ` • PLZ ${regionalInfo.postalCode}`}
                </p>
              )}
            </div>
          </div>
          {renderArticleList(regionalArticles)}
        </div>
      )}

      {/* Info message if no regional news */}
      {regionalArticles.length === 0 && regionalInfo?.message && (
        <div className="mb-8 bg-yellow-50 border-2 border-yellow-200 rounded-2xl p-6">
          <p className="text-lg text-yellow-800">
            ℹ️ {regionalInfo.message}
          </p>
        </div>
      )}

      {/* Global News Section */}
      <div>
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          🌍 Allgemeine Nachrichten
        </h2>
        {globalArticles.length > 0 ? (
          renderArticleList(globalArticles)
        ) : (
          <div className="text-center py-12">
            <Newspaper className="w-24 h-24 mx-auto mb-4 text-gray-400" />
            <p className="text-2xl text-gray-600">Keine Nachrichten verfügbar</p>
          </div>
        )}
      </div>
    </div>
  );
}
